import type { MouseEvent, ReactNode } from 'react';

import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { chipSizes, getIconPixels } from '@/theme/size-tokens';
import type { ColorVariant } from '@/theme/tokens';

export type ChipSize = keyof typeof chipSizes;

export type ChipProps = {
  children: ReactNode;
  className?: string;
  variant?: ColorVariant;
  size?: ChipSize;
  onRemove?: (e: MouseEvent<HTMLButtonElement>) => void;
  icon?: LucideIcon;
  disabled?: boolean;
  clickable?: boolean;
  wrap?: boolean;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
};

const variantText: Record<ColorVariant, string> = {
  neutral: 'text-foreground',
  muted: 'text-muted-foreground',
  primary: 'text-primary',
  teal: 'text-teal',
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
};

const variantBg: Record<ColorVariant, string> = {
  neutral: 'bg-neutral/5',
  muted: 'bg-muted/5',
  primary: 'bg-primary/5',
  teal: 'bg-teal/5',
  info: 'bg-info/5',
  success: 'bg-success/5',
  warning: 'bg-warning/5',
  error: 'bg-error/5',
};

const variantBorder: Record<ColorVariant, string> = {
  neutral: 'border-foreground',
  muted: 'border-muted',
  primary: 'border-primary',
  teal: 'border-teal',
  info: 'border-info',
  success: 'border-success',
  warning: 'border-warning',
  error: 'border-error',
};

const removeSizeClasses: Partial<Record<ChipSize, string>> = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

function Chip({
  children,
  className,
  variant = 'neutral',
  size = 'md',
  onRemove,
  icon: IconComp,
  disabled = false,
  clickable = false,
  wrap = false,
  onClick,
}: Readonly<ChipProps>) {
  const sizeClasses = chipSizes[size].classes;
  const iconPx = getIconPixels(size, 'desktop');

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!disabled && clickable && onClick) {
      onClick(e);
    }
  };

  const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!disabled && onRemove) {
      onRemove(e);
    }
  };

  return (
    <div
      data-slot="chip"
      className={cn(
        'inline-flex items-center justify-center rounded-full border font-medium',
        sizeClasses,
        variantText[variant],
        variantBg[variant],
        variantBorder[variant],
        clickable &&
          !disabled &&
          'cursor-pointer hover:opacity-80 active:opacity-60 transition-opacity',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={handleClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable && !disabled ? 0 : undefined}
      onKeyDown={
        clickable && !disabled
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.(e as unknown as MouseEvent<HTMLDivElement>);
              }
            }
          : undefined
      }
    >
      {IconComp && (
        <span className="flex shrink-0 items-center">
          <IconComp size={iconPx} />
        </span>
      )}
      <span
        className={
          wrap
            ? 'cursor-default break-all whitespace-normal'
            : 'cursor-default truncate'
        }
      >
        {children}
      </span>
      {onRemove && (
        <button
          type="button"
          onClick={handleRemove}
          disabled={disabled}
          className={cn(
            'hover:bg-foreground/10 flex shrink-0 items-center justify-center rounded-full transition-colors',
            removeSizeClasses[size] ?? removeSizeClasses.md,
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
          aria-label="Remove"
        >
          <X size={iconPx} />
        </button>
      )}
    </div>
  );
}

export { Chip };
export default Chip;
